<template>
  <div class="participants">
    <h3>Your Name</h3>
    <div class="current-user">
      <input
        v-model="editableName"
        @blur="updateName"
        @keyup.enter="$event.target.blur()"
        class="name-input"
        placeholder="Enter your name"
      />
    </div>

    <h3 class="participants-title">Participants ({{ participants.length }})</h3>
    <div
      class="participant"
      v-for="participant in participants"
      :style="styleBindingParticipant(participant)"
      :key="participant.id"
    >
      <svg height="14" width="14">
        <circle cx="7" cy="7" r="7" fill="#1a1a1a" />
      </svg>
      <p>{{ participant.name }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { instance } from "../api";

export default {
  setup() {
    const store = useStore();
    const route = useRoute();

    // Get current user's name from the participants list
    const currentUserID = computed(() => store.state.userID);
    const currentUser = computed(() => {
      const users = store.getters.getParticipantList;
      return users.find((u) => u.id === currentUserID.value);
    });

    const editableName = ref("");

    // Watch for current user changes and update editable name
    watch(
      currentUser,
      (user) => {
        if (user) {
          editableName.value = user.name;
        }
      },
      { immediate: true }
    );

    const updateName = async () => {
      if (
        !editableName.value ||
        editableName.value === currentUser.value?.name
      ) {
        return;
      }

      try {
        // Update the user's name in the backend
        await instance.put(`/${route.params.id}/updateuser`, {
          userID: currentUserID.value,
          newName: editableName.value,
        });

        // Update in local store
        if (currentUser.value) {
          currentUser.value.name = editableName.value;
        }
      } catch (error) {
        console.error("Error updating name:", error);
        // Revert to original name on error
        if (currentUser.value) {
          editableName.value = currentUser.value.name;
        }
      }
    };

    const participants = computed(() => store.getters.getParticipantList);

    const styleBindingParticipant = (participant) => {
      if (!store.state.currentHover.includes(participant.id)) {
        return {
          "text-decoration": "line-through",
          opacity: "0.5",
        };
      }
    };

    return {
      editableName,
      updateName,
      participants,
      styleBindingParticipant,
    };
  },
};
</script>

<style scoped>
.participants {
  border-radius: 12px;
  background-color: #ffffff;
  width: 100%;
  padding: 1.5rem;
  border: 1px solid #f5f5f5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
}

.participants h3 {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  color: #737373;
  margin-top: 0;
  margin-left: 0;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.participants-title {
  margin-top: 1.5rem !important;
}

.current-user {
  margin-bottom: 1rem;
}

.name-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.95rem;
  font-weight: 500;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #1a1a1a;
}

.name-input:hover {
  border-color: #d4d4d4;
  background: #ffffff;
}

.name-input:focus {
  background: #ffffff;
  border-color: #a3a3a3;
  outline: none;
}

.participant {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.participant:hover {
  background: #fafafa;
}

.participant p {
  display: inline;
  margin-left: 0.625rem;
  color: #404040;
  font-size: 0.875rem;
}

.participant svg circle {
  fill: #1a1a1a;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

@media (max-width: 768px) {
  .participants {
    padding: 1.25rem;
  }

  .participants h3 {
    font-size: 0.8rem;
  }

  .name-input {
    font-size: 0.9rem;
  }

  .participant p {
    font-size: 0.8rem;
  }
}
</style>
